import { useState, useEffect } from 'react';
import { dropzoneIcon, arrowDownIcon, binIcon } from '../../utils/images';
import { 
  Scenario, 
  ScenarioItem as ScenarioItemType,
  DraggedItemState, 
  ActionRow
} from '../../types/Scenario';
import { useSensors } from '../../contexts/SensorContext';
import { useProducts } from '../../contexts/ProductsContext';
import { useDisplays } from '../../contexts/DisplaysContext';
import { useMediaLibrary } from '../../contexts/MediaLibraryContext';
import { searchProducts, getProductById } from '../../services/productsList';
import { searchImages } from '../../services/mediaList';
import './ScenarioItem.scss';

const VOICE_COMMANDS = [
  { text: "DEFAULT" },
  { text: "CUSTOM" }
];

interface ScenarioItemProps {
  scenario: Scenario;
  draggedItem: DraggedItemState | null;
  updateScenario: (updatedScenario: Scenario | null) => void;
}

export const ScenarioItem: React.FC<ScenarioItemProps> = ({ 
  scenario, 
  draggedItem, 
  updateScenario 
}) => {
  const { getSensorsByType, getSensorById } = useSensors();
  const { products, loading: productsLoading, currentPage: productCurrentPage, totalPages: productTotalPages, goToPage: goToProductPage } = useProducts();
  const { displays, loading: loadingDisplays } = useDisplays();
  const { mediaLibraryData, getAuthenticatedImage, currentPage: mediaCurrentPage, totalPages: mediaTotalPages, goToPage: goToMediaPage } = useMediaLibrary();
  const [authenticatedImages, setAuthenticatedImages] = useState<Record<string, string>>({});
  const [authenticatedProductImages, setAuthenticatedProductImages] = useState<Record<string, string>>({});
  const [productSearchQuery, setProductSearchQuery] = useState<Record<string, string>>({});
  const [imageSearchQuery, setImageSearchQuery] = useState<Record<string, string>>({});
  const [searchResults, setSearchResults] = useState<Record<string, any[]>>({});
  const [isSearching, setIsSearching] = useState<Record<string, boolean>>({});
  const [activeSearch, setActiveSearch] = useState<Record<string, boolean>>({});
  const [customVoiceCommands, setCustomVoiceCommands] = useState<Record<number, string>>({});
  
  const [showDropTarget, setShowDropTarget] = useState({
    condition: false,
    actionDisplay: {} as Record<string, boolean>,
    actionContent: {} as Record<string, boolean>
  });
  
  const [openDropdowns, setOpenDropdowns] = useState({
    conditions: {} as Record<number, boolean>,
    conditionsMmwave: {} as Record<number, boolean>,
    conditionsVoice: {} as Record<number, boolean>,
    actionDisplays: {} as Record<string, boolean>,
    actionContents: {} as Record<string, boolean>
  });

  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [debounceTimeouts, setDebounceTimeouts] = useState<Record<string, NodeJS.Timeout>>({});

  // Authenticate media library images
  useEffect(() => {
    const loadAuthenticatedImages = async () => {
      if (mediaLibraryData.images.length === 0) return;

      const imagePromises = mediaLibraryData.images.map(async (mediaImage) => {
        try {
          const authenticatedUrl = await getAuthenticatedImage(mediaImage.url);
          return { originalUrl: mediaImage.url, authenticatedUrl };
        } catch (error) {
          console.error(`Error loading image ${mediaImage.name}:`, error);
          return null;
        }
      });

      const results = await Promise.all(imagePromises);
      const imageMap: {[key: string]: string} = {};
      
      results.forEach(result => {
        if (result) {
          imageMap[result.originalUrl] = result.authenticatedUrl;
        }
      });

      setAuthenticatedImages(imageMap);
    };

    loadAuthenticatedImages();
  }, [mediaLibraryData.images, getAuthenticatedImage]);

  useEffect(() => {
    const loadAuthenticatedProductImages = async () => {
      if (products.length === 0) return;

      const productImagePromises: Promise<{ originalUrl: string; authenticatedUrl: string } | null>[] = [];

      products.forEach(product => {
        if (product.base_image_url && product.base_image_url.length > 0) {
          product.base_image_url.forEach(imageUrl => {
            productImagePromises.push(
              (async () => {
                try {
                  const authenticatedUrl = await getAuthenticatedImage(imageUrl);
                  return { originalUrl: imageUrl, authenticatedUrl };
                } catch (error) {
                  console.error(`Error loading product image ${imageUrl}:`, error);
                  return null;
                }
              })()
            );
          });
        }
      });

      if (productImagePromises.length === 0) return;

      const results = await Promise.all(productImagePromises);
      const imageMap: Record<string, string> = {};

      results.forEach(result => {
        if (result) {
          imageMap[result.originalUrl] = result.authenticatedUrl;
        }
      });

      setAuthenticatedProductImages(imageMap);
    };

    loadAuthenticatedProductImages();
  }, [products]);

  useEffect(() => {
    const authenticateSearchImages = async () => {
      const imageSearchResults: any[] = [];
      Object.values(searchResults).forEach(results => {
        results.forEach(result => {
          if (result.url && result.name) {
            imageSearchResults.push(result);
          }
        });
      });

      if (imageSearchResults.length === 0) return;

      const imagePromises = imageSearchResults.map(async (img) => {
        try {
          const authenticatedUrl = await getAuthenticatedImage(img.url);
          return { originalUrl: img.url, authenticatedUrl };
        } catch (error) {
          console.error(`Error loading search image ${img.name}:`, error);
          return null;
        }
      });

      const results = await Promise.all(imagePromises);
      const imageMap: Record<string, string> = {};

      results.forEach(result => {
        if (result) {
          imageMap[result.originalUrl] = result.authenticatedUrl;
        }
      });

      setAuthenticatedImages(prev => ({ ...prev, ...imageMap }));
    };

    authenticateSearchImages();
  }, [searchResults, getAuthenticatedImage]);

  // Handle input change with debouncing for conditions
  const handleInputChange = (itemId: number, value: string) => {
    const key = `condition-${itemId}`;
    setInputValues(prev => ({ ...prev, [key]: value }));
    
    if (debounceTimeouts[key]) {
      clearTimeout(debounceTimeouts[key]);
    }
    
    const timeoutId = setTimeout(() => {
      updateConditionValue(itemId, value);
      setDebounceTimeouts(prev => {
        const newTimeouts = { ...prev };
        delete newTimeouts[key];
        return newTimeouts;
      });
    }, 500);
    
    setDebounceTimeouts(prev => ({ ...prev, [key]: timeoutId }));
  };

  const getCurrentInputValue = (item: ScenarioItemType): string => {
    const key = `condition-${item.id}`;
    return inputValues[key] !== undefined ? inputValues[key] : item.value;
  };

  useEffect(() => {
    return () => {
      Object.values(debounceTimeouts).forEach(timeout => clearTimeout(timeout));
    };
  }, [debounceTimeouts]);

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent, zone: 'condition' | 'actionDisplay' | 'actionContent', rowId?: string): void => {
    e.preventDefault();
    if (!draggedItem) return;
    
    // Field restrictions
    if (zone === 'actionDisplay' && draggedItem.type !== 'display') return;
    if (zone === 'actionContent' && !['product', 'image'].includes(draggedItem.type)) return;
    
    if (zone === 'condition') {
      setShowDropTarget(prev => ({ ...prev, condition: true }));
    } else if (rowId !== undefined) {
      setShowDropTarget(prev => ({
        ...prev,
        [zone]: { ...prev[zone], [rowId]: true }
      }));
    }
  };

  const handleDragLeave = (e: React.DragEvent, zone: 'condition' | 'actionDisplay' | 'actionContent', rowId?: string): void => {
    e.preventDefault();
    
    if (zone === 'condition') {
      setShowDropTarget(prev => ({ ...prev, condition: false }));
    } else if (rowId !== undefined) {
      setShowDropTarget(prev => ({
        ...prev,
        [zone]: { ...prev[zone], [rowId]: false }
      }));
    }
  };

  const handleDrop = (e: React.DragEvent, zone: 'condition' | 'actionDisplay' | 'actionContent', rowId?: string): void => {
    e.preventDefault();
    
    if (zone === 'condition') {
      setShowDropTarget(prev => ({ ...prev, condition: false }));
    } else if (rowId !== undefined) {
      setShowDropTarget(prev => ({
        ...prev,
        [zone]: { ...prev[zone], [rowId]: false }
      }));
    }

    if (!draggedItem) return;
    
    if (zone === 'condition') {
      // Handle condition drop
    const newItem: ScenarioItemType = {
      id: Date.now(),
      type: draggedItem.type,
      name: getInitialName(draggedItem),
      value: getInitialValue(draggedItem),
      ...(draggedItem.type !== 'action' && draggedItem.type !== 'formula' && { mac: "" })
    };

      const conditionItems = scenario.conditions || [];
      updateScenario({
        ...scenario,
        conditions: [...conditionItems, newItem]
      });
    } else if ((zone === 'actionDisplay' || zone === 'actionContent') && rowId !== undefined) {
      // Handle action field drop - update only specific field
      const actionRows = scenario.actions || [];
      const updatedActionRows = actionRows.map(row => {
        if (row.id === rowId) {
          if (zone === 'actionDisplay') {
            // Update display field (target)
            return {
              ...row,
              target: getInitialTarget(draggedItem)
            };
          } else if (zone === 'actionContent') {
            // Update content field (type and value)
            const actionType = getActionType(draggedItem);
            const actionValue = getInitialActionValue(draggedItem);
            console.log('Dropping action content - type:', actionType, 'value:', actionValue, 'draggedItem:', draggedItem);
          return {
            ...row,
              type: actionType,
              value: actionValue
          };
          }
        }
        return row;
      });
      
      updateScenario({
        ...scenario,
        actions: updatedActionRows
      });
    }
  };

  // Helper functions
  const getInitialName = (draggedItem: DraggedItemState): string => {
    if (draggedItem.type === 'sensor') {
      // Return the sensor type as a string
      return String(draggedItem.item.name);
    }
    return draggedItem.item.name;
  };

  const getInitialValue = (draggedItem: DraggedItemState): string => {
    if (draggedItem.type === 'sensor' && draggedItem.item.defaultValue) {
      return draggedItem.item.defaultValue;
    } else if (draggedItem.type === 'sensor' && draggedItem.item.name.toLowerCase().includes('voice')) {
      return 'DEFAULT';
    } else if (draggedItem.type === 'action' || draggedItem.type === 'formula') {
      return draggedItem.item.name;
    }
    return "";
  };

  const getActionType = (draggedItem: DraggedItemState): 'product' | 'image' => {
    return draggedItem.type as 'product' | 'image';
  };

  const getInitialTarget = (draggedItem: DraggedItemState): string[] => {
    if (draggedItem.type === 'display') {
      // Use the first available display ID if displays are loaded
      if (displays.length > 0) {
        return [String(displays[0].id)];
      }
      return [];
    }
    return [];
  };

  const getInitialActionValue = (draggedItem: DraggedItemState): (string | number)[] => {
    if (draggedItem.type === 'product') {
      // For products, we'll set a default value and let the user select from dropdown
      // Use first available product ID if products are loaded, otherwise use a default
      if (products.length > 0) {
        return [products[0].id];
      } else {
        // If products aren't loaded yet, use a default that will be replaced
        return [1];
      }
    } else if (draggedItem.type === 'image') {
      // For images, use the first media library image URL if available
      if (mediaLibraryData.images.length > 0) {
        return [mediaLibraryData.images[0].url];
      }
      return [''];
    }
    return [];
  };

  // Action handlers
  const removeCondition = (conditionId: number): void => {
    updateScenario({
      ...scenario,
      conditions: (scenario.conditions || []).filter(condition => condition.id !== conditionId)
    });
  };
  
  const removeDisplayFromAction = (rowId: string): void => {
    const actionRows = scenario.actions || [];
    const updatedActionRows = actionRows.map(row => {
      if (row.id === rowId) {
        return {
          ...row,
          target: []
        };
      }
      return row;
    });
    
    updateScenario({
      ...scenario,
      actions: updatedActionRows
    });
  };

  const removeContentFromAction = (rowId: string): void => {
    const actionRows = scenario.actions || [];
    const updatedActionRows = actionRows.map(row => {
      if (row.id === rowId) {
        return {
          ...row,
          type: "product" as const,
          value: []
        };
      }
      return row;
    });
    
    updateScenario({
      ...scenario,
      actions: updatedActionRows
    });
  };

  const ensureActionRow = (): string => {
    if (!scenario.actions || scenario.actions.length === 0) {
      const newRowId = `action-${Date.now()}`;
      const newRow: ActionRow = {
        id: newRowId,
        target_type: "display",
        target: [],
        type: "product",
        value: []
      };
    updateScenario({
      ...scenario,
        actions: [newRow]
      });
      return newRowId;
    }
    return scenario.actions[0].id;
  };

  const addActionRow = (): void => {
    const actionRows = scenario.actions || [];
    const newRowId = `action-${Date.now()}`;
    const newRow: ActionRow = {
      id: newRowId,
      target_type: "display",
      target: [],
      type: "product",
      value: []
    };
    updateScenario({
      ...scenario,
      actions: [...actionRows, newRow]
    });
  };

  const removeActionRow = (rowId: string): void => {
    updateScenario({
      ...scenario,
      actions: (scenario.actions || []).filter(row => row.id !== rowId)
    });
  };

  const updateConditionName = (itemId: number, newSensorId: string): void => {
    // Get the sensor by ID to extract MAC address and sensor type
    const sensor = getSensorById(newSensorId);
    
    updateScenario({
      ...scenario,
      conditions: (scenario.conditions || []).map(condition => 
          condition.id === itemId 
            ? { 
                ...condition, 
                name: String(sensor?.sensor_type || ''),
                mac: sensor?.mac_address || ''
              }
          : condition
      )
    });
  };

  const updateConditionValue = (itemId: number, newValue: string): void => {
    updateScenario({
      ...scenario,
      conditions: (scenario.conditions || []).map(condition => 
        condition.id === itemId 
          ? { ...condition, value: newValue }
        : condition
      )
    });
  };

  const updateCameraWithMmwave = (itemId: number, mmwaveSensorId: string): void => {
    // Get the mmwave sensor to extract its MAC address
    const mmwaveSensor = getSensorById(mmwaveSensorId);
    
    updateScenario({
      ...scenario,
      conditions: (scenario.conditions || []).map(condition => 
        condition.id === itemId 
          ? { ...condition, value: mmwaveSensor?.mac_address || '' }
        : condition
      )
    });
  };

  const updateVoiceCommandSelection = (itemId: number, command: string): void => {
    updateScenario({
      ...scenario,
      conditions: (scenario.conditions || []).map(condition => 
        condition.id === itemId 
          ? { ...condition, value: command }
        : condition
      )
    });
  };

  const updateActionDisplaySelection = (rowId: string, selectedValue: string): void => {
    const actionRows = scenario.actions || [];
    const updatedActionRows = actionRows.map(row => {
      if (row.id === rowId) {
        return {
          ...row,
          target: [selectedValue]
        };
      }
      return row;
    });
    
    updateScenario({
      ...scenario,
      actions: updatedActionRows
    });
  };

  const updateActionContentSelection = (rowId: string, selectedValue: string, itemType: 'product' | 'image'): void => {
    const actionRows = scenario.actions || [];
    const updatedActionRows = actionRows.map(row => {
        if (row.id === rowId) {
        let newValue: (string | number)[];
        if (itemType === 'product') {
          newValue = [parseInt(selectedValue)];
        } else {
          // For images, selectedValue is the original URL from cached images
          newValue = [selectedValue];
        }
        
          return {
            ...row,
          type: itemType,
          value: newValue
          };
        }
        return row;
      });
      
      updateScenario({
        ...scenario,
        actions: updatedActionRows
      });
  };

  const toggleDropdown = (type: 'condition' | 'conditionMmwave' | 'conditionVoice' | 'actionDisplay' | 'actionContent', id: number | string): void => {
    if (type === 'condition') {
      const numId = id as number;
      setOpenDropdowns(prev => ({
        ...prev,
        conditions: {
          ...prev.conditions,
          [numId]: !prev.conditions[numId]
        }
      }));
    } else if (type === 'conditionMmwave') {
      const numId = id as number;
      setOpenDropdowns(prev => ({
        ...prev,
        conditionsMmwave: {
          ...prev.conditionsMmwave,
          [numId]: !prev.conditionsMmwave[numId]
        }
      }));
    } else if (type === 'conditionVoice') {
      const numId = id as number;
      setOpenDropdowns(prev => ({
        ...prev,
        conditionsVoice: {
          ...prev.conditionsVoice,
          [numId]: !prev.conditionsVoice[numId]
        }
      }));
    } else {
      const strId = id as string;
      const targetKey = type === 'actionDisplay' ? 'actionDisplays' : 'actionContents';
      setOpenDropdowns(prev => ({
        ...prev,
        [targetKey]: {
          ...prev[targetKey],
          [strId]: !prev[targetKey][strId]
        }
      }));
    }
  };

  const closeDropdown = (type: 'condition' | 'conditionMmwave' | 'conditionVoice' | 'actionDisplay' | 'actionContent', id: number | string): void => {
    if (type === 'condition') {
      const numId = id as number;
      setOpenDropdowns(prev => ({
        ...prev,
        conditions: {
          ...prev.conditions,
          [numId]: false
        }
      }));
    } else if (type === 'conditionMmwave') {
      const numId = id as number;
      setOpenDropdowns(prev => ({
        ...prev,
        conditionsMmwave: {
          ...prev.conditionsMmwave,
          [numId]: false
        }
      }));
    } else if (type === 'conditionVoice') {
      const numId = id as number;
      setOpenDropdowns(prev => ({
        ...prev,
        conditionsVoice: {
          ...prev.conditionsVoice,
          [numId]: false
        }
      }));
    } else {
      const strId = id as string;
      const targetKey = type === 'actionDisplay' ? 'actionDisplays' : 'actionContents';
      setOpenDropdowns(prev => ({
        ...prev,
        [targetKey]: {
          ...prev[targetKey],
          [strId]: false
        }
      }));
    }
  };

  const getSensorOptions = (sensorType: string) => {
    const sensorsOfType = getSensorsByType(sensorType);
    return sensorsOfType.map(sensor => ({id: sensor.id, mac: sensor.mac_address }));
  };

  const sensorNeedsTextInput = (sensorType: string): boolean => {
    return ['rfid'].includes(sensorType.toLowerCase());
  };

  const sensorNeedsVoiceDropdown = (sensorType: string): boolean => {
    return ['voice'].includes(sensorType.toLowerCase());
  };


  const getSensorPlaceholder = (sensorType: string): string => {
    const lowerType = sensorType.toLowerCase();
    if (lowerType.includes('rfid')) {
        return 'Enter RFID code';
    } else if (lowerType.includes('voice')) {
        return 'Enter voice command';
    } else {
        return 'Enter value';
    }
  };



  const handleProductSearch = async (rowId: string) => {
    const query = productSearchQuery[rowId] || '';
    
    if (query.trim() === '') {
      setSearchResults(prev => ({ ...prev, [rowId]: [] }));
      setActiveSearch(prev => ({ ...prev, [rowId]: false }));
      return;
    }

    setIsSearching(prev => ({ ...prev, [rowId]: true }));
    try {
      const results = await searchProducts(query);
      setSearchResults(prev => ({ ...prev, [rowId]: results }));
      setActiveSearch(prev => ({ ...prev, [rowId]: true }));
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults(prev => ({ ...prev, [rowId]: [] }));
      setActiveSearch(prev => ({ ...prev, [rowId]: false }));
    } finally {
      setIsSearching(prev => ({ ...prev, [rowId]: false }));
    }
  };

  const handleImageSearch = async (rowId: string) => {
    const query = imageSearchQuery[rowId] || '';
    
    if (query.trim() === '') {
      setSearchResults(prev => ({ ...prev, [rowId]: [] }));
      setActiveSearch(prev => ({ ...prev, [rowId]: false }));
      return;
    }

    setIsSearching(prev => ({ ...prev, [rowId]: true }));
    try {
      const results = await searchImages(query);
      setSearchResults(prev => ({ ...prev, [rowId]: results }));
      setActiveSearch(prev => ({ ...prev, [rowId]: true }));
    } catch (error) {
      console.error('Error searching images:', error);
      setSearchResults(prev => ({ ...prev, [rowId]: [] }));
      setActiveSearch(prev => ({ ...prev, [rowId]: false }));
    } finally {
      setIsSearching(prev => ({ ...prev, [rowId]: false }));
    }
  };

  const [productNameCache, setProductNameCache] = useState<Record<number, string>>({});

  useEffect(() => {
    const loadProductNames = async () => {
      if (!scenario.actions || scenario.actions.length === 0) return;

      const productIds = new Set<number>();
      scenario.actions.forEach(action => {
        if (action.type === 'product' && action.value.length > 0) {
          productIds.add(Number(action.value[0]));
        }
      });

      const newCache = { ...productNameCache };
      const productIdArray = Array.from(productIds);
      
      for (let i = 0; i < productIdArray.length; i++) {
        const productId = productIdArray[i];
        if (!newCache[productId]) {
          const foundProduct = products.find(p => p.id === productId);
          if (foundProduct) {
            newCache[productId] = foundProduct.name;
          } else {
            try {
              const product = await getProductById(productId);
              if (product) {
                newCache[productId] = product.name;
              }
            } catch (error) {
              console.error(`Error loading product name for ID ${productId}:`, error);
            }
          }
        }
      }
      setProductNameCache(newCache);
    };

    loadProductNames();
  }, [scenario.actions, products]);

  const getActionDisplayName = (actionType: string, value: string | number): string => {
    switch (actionType) {
      case 'display':
        if (loadingDisplays) {
          return 'Loading...';
        }
        const display = displays.find(d => d.id === Number(value));
        return display ? `Display ${display.id} (${display.android_id})` : `Display ID: ${value}`;
      case 'product':
        const productId = Number(value);
        return productNameCache[productId] || `Product ID: ${value}`;
      case 'image':
        // For images, try to find the name from media library first
        const urlString = String(value);
        const mediaImage = mediaLibraryData.images.find(img => img.url === urlString);
        if (mediaImage) {
          return mediaImage.name.length > 30 ? mediaImage.name.substring(0, 30) + '...' : mediaImage.name;
        }

        const filename = urlString.split('/').pop() || urlString;
        return filename.length > 30 ? filename.substring(0, 30) + '...' : filename;
      default:
        return String(value);
    }
  };

  const getStyleClass = (type: string): string => {
    switch (type) {
      case 'action':
        return 'scenario__chip--action';
      case 'display':
        return 'scenario__chip--display';
      case 'sensor':
        return 'scenario__chip--sensor';
      case 'formula':
        return 'scenario__chip--formula';
      case 'product':
        return 'scenario__chip--product';
      case 'image':
        return 'scenario__chip--image';
      default:
        return '';
    }
  };

  const renderConditionItem = (item: ScenarioItemType) => {
    // The name is now the sensor type directly
    const originalSensorType = item.type === 'sensor' ? String(item.name) : '';

    const hasDropdown = item.type === 'sensor' && getSensorOptions(originalSensorType).length > 0;
    const needsTextInput = item.type === 'sensor' && sensorNeedsTextInput(originalSensorType);
    const needsVoiceDropdown = item.type === 'sensor' && sensorNeedsVoiceDropdown(originalSensorType);
    const isDropdownOpen = openDropdowns.conditions[item.id];
    const isVoiceDropdownOpen = openDropdowns.conditionsVoice[item.id];

      return (
      <div className="scenario__item-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
        {hasDropdown ? (
          <div className="scenario__dropdown-field">
            <div 
              className={`scenario__chip ${getStyleClass(item.type)} scenario__chip--dropdown`}
              // onClick={() => toggleDropdown('condition', item.id)}
            >
              {item.name} {item.mac ? `(${item.mac})` : '' }
              <img src={arrowDownIcon} alt="Dropdown" className="scenario__chip-dropdown-icon" onClick={() => toggleDropdown('condition', item.id)}></img>
              {originalSensorType === 'camera' && (
                <>
                  <span>mmwave ({item.value && (item.value)})</span> <img src={arrowDownIcon} alt="Mmwave Dropdown" className="scenario__chip-dropdown-icon" onClick={() => toggleDropdown('conditionMmwave', item.id)}></img>
                </>
              )}
            </div>
            
            {isDropdownOpen && (
              <div className="scenario__dropdown-options">
                {getSensorOptions(originalSensorType).map((option, idx) => (
                  <div 
                    key={idx} 
                    className="scenario__dropdown-option"
                    onClick={() => {
                      updateConditionName(item.id, option.id);
                      closeDropdown('condition', item.id);
                    }}
                  >
                    {option.mac}
                  </div>
                ))}
              </div>
            )}

            {originalSensorType === 'camera' && openDropdowns.conditionsMmwave[item.id] && (
              <div className="scenario__dropdown-options">
                {getSensorOptions('mmwave').map((mmwaveOption, idx) => (
                  <div 
                    key={idx} 
                    className="scenario__dropdown-option"
                    onClick={() => {
                      updateCameraWithMmwave(item.id, mmwaveOption.id);
                      closeDropdown('conditionMmwave', item.id);
                    }}
                  >
                    {mmwaveOption.mac}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className={`scenario__chip ${getStyleClass(item.type)}`}>
            {item.name}
          </div>
        )}

        {item.type === 'sensor' && (originalSensorType.toLowerCase().includes('mmwave') || needsTextInput || needsVoiceDropdown) && (
          <>
            {needsTextInput ? (
              <input
                type="text"
                className="scenario__chip scenario__chip--input-field"
                value={getCurrentInputValue(item)}
                onChange={(e) => handleInputChange(item.id, e.target.value)}
                placeholder={getSensorPlaceholder(originalSensorType)}
                style={{ minWidth: '120px', maxWidth: '200px' }}
              />
            ) : needsVoiceDropdown ? (
              <div className="scenario__dropdown-field">
                <div 
                  className={`scenario__chip ${getStyleClass('action')} scenario__chip--dropdown`}
                  onClick={() => toggleDropdown('conditionVoice', item.id)}
                >
                  {item.value}
                  <img src={arrowDownIcon} alt="Voice Dropdown" className="scenario__chip-dropdown-icon" />
                </div>
                
                {isVoiceDropdownOpen && (
                  <div className="scenario__dropdown-options">
                    {VOICE_COMMANDS.map((command, idx) => (
                      <div 
                        key={idx} 
                        className="scenario__dropdown-option"
                        onClick={() => {
                          updateVoiceCommandSelection(item.id, command.text);
                          if (command.text !== 'CUSTOM') {
                            closeDropdown('conditionVoice', item.id);
                          }
                        }}
                      >
                        {command.text}
                      </div>
                    ))}
                    {item.value === 'CUSTOM' && (
                      <input
                        type="text"
                        className="scenario__chip scenario__chip--input-field"
                        value={customVoiceCommands[item.id] || ''}
                        onChange={(e) => {
                          setCustomVoiceCommands(prev => ({ ...prev, [item.id]: e.target.value }));
                        }}
                        onBlur={(e) => {
                          updateVoiceCommandSelection(item.id, e.target.value);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        placeholder="Enter custom command"
                        style={{ minWidth: '300px', maxWidth: '500px', margin: '4px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}
                      />
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className={`scenario__chip ${getStyleClass('action')}`}>
                {item.value}
              </div>
            )}
          </>
        )}

        <button 
          className="scenario__remove-button" 
          onClick={() => removeCondition(item.id)}
        >
          ×
        </button>
        </div>
      );
  };

  const renderActionDisplayField = (actionRow: ActionRow | undefined, rowId: string) => {
    if (actionRow && actionRow.target.length > 0) {
      const isDropdownOpen = openDropdowns.actionDisplays[rowId];
      const displayName = getActionDisplayName('display', actionRow.target[0]);
      
      return (
        <div className="scenario__item-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
          <div className="scenario__dropdown-field">
            <div 
              className={`scenario__chip ${getStyleClass('display')} scenario__chip--dropdown`}
              onClick={() => toggleDropdown('actionDisplay', rowId)}
            >
              {displayName}
              <img src={arrowDownIcon} alt="Dropdown" className="scenario__chip-dropdown-icon" />
            </div>
            
            {isDropdownOpen && (
              <div className="scenario__dropdown-options">
                {loadingDisplays ? (
                  <div className="scenario__dropdown-option">Loading displays...</div>
                ) : displays.map(display => (
                  <div 
                    key={display.id} 
                    className="scenario__dropdown-option"
                    onClick={() => {
                      updateActionDisplaySelection(rowId, String(display.id));
                      closeDropdown('actionDisplay', rowId);
                    }}
                  >
                    Display {display.id} ({display.android_id})
                  </div>
                ))}
              </div>
            )}
          </div>

          <button 
            className="scenario__remove-button" 
            onClick={() => removeDisplayFromAction(rowId)}
          >
            ×
          </button>
        </div>
      );
    }

    return (
      <div 
        className={`scenario__action-dropzone ${showDropTarget.actionDisplay[rowId] ? 'active' : ''}`}
        onDragOver={(e) => handleDragOver(e, 'actionDisplay', rowId)}
        onDragLeave={(e) => handleDragLeave(e, 'actionDisplay', rowId)}
        onDrop={(e) => handleDrop(e, 'actionDisplay', rowId)}
      >
        <img className="scenario__dropzone-icon" src={dropzoneIcon} alt="Drop Icon" />
        <span className="scenario__placeholder">Drag display here</span>
      </div>
    );
  };

  const renderActionContentField = (actionRow: ActionRow | undefined, rowId: string) => {
    if (actionRow && actionRow.value.length > 0) {
      const isDropdownOpen = openDropdowns.actionContents[rowId];
      let displayName = '';
      let options: any[] = [];
      
      if (actionRow.type === 'product') {
        displayName = getActionDisplayName('product', actionRow.value[0]);
        const hasSearchResults = searchResults[rowId] && searchResults[rowId].length > 0;
        options = hasSearchResults ? searchResults[rowId] : products;
      } else if (actionRow.type === 'image') {
        const imageUrl = actionRow.value[0] as string;
        displayName = getActionDisplayName('image', imageUrl);
        const hasSearchResults = searchResults[rowId] && searchResults[rowId].length > 0;
        const imagesToShow = hasSearchResults ? searchResults[rowId] : mediaLibraryData.images;
        options = imagesToShow.map(img => ({
          url: img.url,
          name: img.name,
          lastModified: img.last_modified
        }));
      }
      
      return (
        <div className="scenario__item-container" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
          <div className="scenario__dropdown-field">
            <div 
              className={`scenario__chip ${getStyleClass(actionRow.type)} scenario__chip--dropdown`}
              onClick={() => toggleDropdown('actionContent', rowId)}
            >
              {displayName}
              <img src={arrowDownIcon} alt="Dropdown" className="scenario__chip-dropdown-icon" />
            </div>
            
            {isDropdownOpen && (
              <>
                {actionRow.type === 'product' && (
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px', padding: '0 0 0 0' }}>
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={productSearchQuery[rowId] || ''}
                      onChange={(e) => setProductSearchQuery(prev => ({ ...prev, [rowId]: e.target.value }))}
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={() => handleProductSearch(rowId)}
                      disabled={isSearching[rowId]}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5',
                        cursor: isSearching[rowId] ? 'not-allowed' : 'pointer',
                        opacity: isSearching[rowId] ? 0.6 : 1,
                        fontSize: '12px',
                        fontWeight: 500
                      }}
                    >
                      {isSearching[rowId] ? 'Searching...' : 'Search'}
                    </button>
                    {productSearchQuery[rowId] && (
                      <button
                        onClick={() => {
                          setProductSearchQuery(prev => ({ ...prev, [rowId]: '' }));
                          setSearchResults(prev => ({ ...prev, [rowId]: [] }));
                          setActiveSearch(prev => ({ ...prev, [rowId]: false }));
                        }}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
                {actionRow.type === 'image' && (
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '4px', padding: '0 0 0 0' }}>
                    <input
                      type="text"
                      placeholder="Search images..."
                      value={imageSearchQuery[rowId] || ''}
                      onChange={(e) => setImageSearchQuery(prev => ({ ...prev, [rowId]: e.target.value }))}
                      style={{
                        flex: 1,
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={() => handleImageSearch(rowId)}
                      disabled={isSearching[rowId]}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        backgroundColor: '#f5f5f5',
                        cursor: isSearching[rowId] ? 'not-allowed' : 'pointer',
                        opacity: isSearching[rowId] ? 0.6 : 1,
                        fontSize: '12px',
                        fontWeight: 500
                      }}
                    >
                      {isSearching[rowId] ? 'Searching...' : 'Search'}
                    </button>
                    {imageSearchQuery[rowId] && (
                      <button
                        onClick={() => {
                          setImageSearchQuery(prev => ({ ...prev, [rowId]: '' }));
                          setSearchResults(prev => ({ ...prev, [rowId]: [] }));
                          setActiveSearch(prev => ({ ...prev, [rowId]: false }));
                        }}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: '#f5f5f5',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold'
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}
                <div className="scenario__dropdown-options">
                  {options.map((option, index) => (
                    <div 
                      key={actionRow.type === 'product' ? option.id : option.url} 
                      className="scenario__dropdown-option"
                      onClick={() => {
                        const selectedValue = actionRow.type === 'product' ? String(option.id) : option.url;
                        updateActionContentSelection(rowId, selectedValue, actionRow.type);
                        closeDropdown('actionContent', rowId);
                      }}
                    >
                      {actionRow.type === 'product' ? (
                        <div className="scenario__dropdown-option-content" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {option.base_image_url && option.base_image_url.length > 0 && (
                            <img 
                              src={authenticatedProductImages[option.base_image_url[0]] || option.base_image_url[0]}
                              alt={option.name}
                              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          )}
                          <div>
                            <div className="scenario__dropdown-option-name">{option.name}</div>
                            {option.ai_tags && (
                              <div className="scenario__dropdown-option-tags">
                                {option.ai_tags.map((tag: string, tagIndex: number) => (
                                  <span key={tagIndex} className="scenario__dropdown-option-tag">#{tag} </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="scenario__dropdown-option-content" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {authenticatedImages[option.url] && (
                            <img 
                              src={authenticatedImages[option.url]}
                              alt="media"
                              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                            />
                          )}
                          <div className="scenario__dropdown-option-name">
                            {(() => {
                              const mediaImage = mediaLibraryData.images.find(img => img.url === option.url);
                              return mediaImage ? mediaImage.name : (option.url.split('/').pop() || 'Unknown');
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {actionRow.type === 'product' && !activeSearch[rowId] && productTotalPages > 1 && (
                  <div style={{ padding: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <button 
                      onClick={() => goToProductPage(productCurrentPage - 1)} 
                      disabled={productCurrentPage === 1} 
                      style={{ padding: '4px 8px', cursor: productCurrentPage === 1 ? 'not-allowed' : 'pointer', opacity: productCurrentPage === 1 ? 0.5 : 1, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f5f5f5', fontSize: '12px' }}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontSize: '12px', color: '#666' }}>Page {productCurrentPage} of {productTotalPages}</span>
                    <button 
                      onClick={() => goToProductPage(productCurrentPage + 1)} 
                      disabled={productCurrentPage === productTotalPages} 
                      style={{ padding: '4px 8px', cursor: productCurrentPage === productTotalPages ? 'not-allowed' : 'pointer', opacity: productCurrentPage === productTotalPages ? 0.5 : 1, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f5f5f5', fontSize: '12px' }}
                    >
                      Next →
                    </button>
                  </div>
                )}
                {actionRow.type === 'image' && !activeSearch[rowId] && mediaTotalPages > 1 && (
                  <div style={{ padding: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                    <button 
                      onClick={() => goToMediaPage(mediaCurrentPage - 1)} 
                      disabled={mediaCurrentPage === 1} 
                      style={{ padding: '4px 8px', cursor: mediaCurrentPage === 1 ? 'not-allowed' : 'pointer', opacity: mediaCurrentPage === 1 ? 0.5 : 1, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f5f5f5', fontSize: '12px' }}
                    >
                      ← Prev
                    </button>
                    <span style={{ fontSize: '12px', color: '#666' }}>Page {mediaCurrentPage} of {mediaTotalPages}</span>
                    <button 
                      onClick={() => goToMediaPage(mediaCurrentPage + 1)} 
                      disabled={mediaCurrentPage === mediaTotalPages} 
                      style={{ padding: '4px 8px', cursor: mediaCurrentPage === mediaTotalPages ? 'not-allowed' : 'pointer', opacity: mediaCurrentPage === mediaTotalPages ? 0.5 : 1, border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f5f5f5', fontSize: '12px' }}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          <button 
            className="scenario__remove-button" 
            onClick={() => removeContentFromAction(rowId)}
          >
            ×
          </button>
        </div>
      );
    }

    return (
      <div 
        className={`scenario__action-dropzone ${showDropTarget.actionContent[rowId] ? 'active' : ''}`}
        onDragOver={(e) => handleDragOver(e, 'actionContent', rowId)}
        onDragLeave={(e) => handleDragLeave(e, 'actionContent', rowId)}
        onDrop={(e) => handleDrop(e, 'actionContent', rowId)}
      >
        <img className="scenario__dropzone-icon" src={dropzoneIcon} alt="Drop Icon" />
        <span className="scenario__placeholder">Drop product/image here</span>
      </div>
    );
  };

  const defaultRowId = ensureActionRow();

  return (
    <div className="scenario__scenario-card">
      <div className="scenario__number-wrapper">
        <div className="scenario__number-wrapper--container">
          <p className="scenario__number-wrapper--container--num">{scenario.id}</p>
        </div>
      </div>
      
      <div className="scenario__scenario-content">
        {/* Condition section */}
        <div className="scenario__scenario-line">
          <div className="scenario__line-title">
            <span className="scenario__label">Scenario:</span>
          </div>
          <div className="scenario__condition-row">
            <span className="scenario__condition-prefix">If the visitor</span>
            
            {(scenario.conditions || []).map(condition => (
              <div key={condition.id} className="scenario__condition-item">
                {renderConditionItem(condition)}
              </div>
            ))}
            
            <div 
              className={`scenario__condition-dropzone ${showDropTarget.condition ? 'active' : ''}`}
              onDragOver={(e) => handleDragOver(e, 'condition')}
              onDragLeave={(e) => handleDragLeave(e, 'condition')}
              onDrop={(e) => handleDrop(e, 'condition')}
            >
              <img className="scenario__dropzone-icon" src={dropzoneIcon} alt="Drop Icon" />
              <span className="scenario__placeholder">Drop feature here</span>
            </div>
          </div>
        </div>
        
        {/* Action section */}
        <div className="scenario__action-section">
          <div className="scenario__line-title">
          <span className="scenario__label">Action:</span>
          </div>
          <div className="scenario__action-row">
          {(scenario.actions || []).map(actionRow => (
            <div key={actionRow.id} className="scenario__action-flow">
            <div className="scenario__action-container">
                {/* Display field */}
              <div className="scenario__action-flow-item">
                  {renderActionDisplayField(actionRow, actionRow.id)}
              </div>
              
              <div className="scenario__action-flow-item scenario__ai-generate">
                <span className="scenario__action-text">show / generate</span>
              </div>
              
                {/* Content field */}
              <div className="scenario__action-flow-item">
                  {renderActionContentField(actionRow, actionRow.id)}
              </div>
            </div>
            
            <button 
              className="scenario__remove-row-button scenario__remove-row-button--inline" 
                onClick={() => removeActionRow(actionRow.id)}
            >
              ×
            </button>
          </div>
            ))}
        </div>
          {/* Show default row if no action rows exist */}
          {(!scenario.actions || scenario.actions.length === 0) && (
            <div className="scenario__action-flow">
              <div className="scenario__action-container">
                {/* Display field */}
                <div className="scenario__action-flow-item">
                  {renderActionDisplayField(undefined, defaultRowId)}
                </div>
                
                <div className="scenario__action-flow-item scenario__ai-generate">
                  <span className="scenario__action-text">show / generate</span>
                </div>
                
                {/* Content field */}
                <div className="scenario__action-flow-item">
                  {renderActionContentField(undefined, defaultRowId)}
                </div>
              </div>
                
              <button 
                className="scenario__remove-row-button scenario__remove-row-button--inline" 
                onClick={() => removeActionRow(defaultRowId)}
              >
                ×
              </button>
            </div>
          )}
          </div>
        
        <button className="scenario__add-action-button" onClick={addActionRow}>
          Add Action <span className="scenario__add-icon">+</span>
        </button>
        
        {/* <div className="scenario__hashtag-line">
          <span className="scenario__label">Hashtag:</span>
          <div className="scenario__hashtags">
          </div>
        </div> */}

      <button className="scenario__delete-button" onClick={() => updateScenario(null)}>
        <img src={binIcon} alt="Delete" />
      </button>
      </div>
    </div>
  );
};